import React, { useState, useEffect } from "react";
import { withRouter } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { Alert } from 'reactstrap';

import {
  bulkApplyProductFeeOption,
  bulkApplyProductTaxRate,
  createProductDefaults,
  updateProductDefaults,
} from "../../../../actions/settings";
import { buildAuthRoleState, canBulkApplyProductDefaults } from '../../../../actions/auth';
import { mockFeeOptions } from '../../../../model/mockProduct';
import s from "./DefaultProduct.module.scss";

const DefaultProduct = ({
  categories,
  storeDetails,
  productDefaults,
  products,
  canBulkApply,
}) => {
  const dispatch = useDispatch();

  const [error, setError] = useState("");
  const [prodDefaults, setProdDefaults] = useState(productDefaults);
  const [categoryList, setCategories] = useState([]);
  const [isApplyingFeeOption, setIsApplyingFeeOption] = useState(false);
  const [isApplyingTaxRate, setIsApplyingTaxRate] = useState(false);

  useEffect(() => {
    setCategories(categories);
  }, [categories]);

  useEffect(() => {
    setProdDefaults({...productDefaults, TaxRate: productDefaults.TaxRate * 100});
  }, [productDefaults]);

  const getFeeOptionName = (feeOptionId) => {
    const match = mockFeeOptions.find((fee) => String(fee.Id) === String(feeOptionId));
    return match ? match.Name : 'selected card fee option';
  };

  const getActiveProductLabel = () => {
    const count = Array.isArray(products) ? products.length : 0;
    if (count > 0) {
      return `${count} active product${count === 1 ? '' : 's'}`;
    }
    return 'all active products';
  };

  const onChangeProdName = (e) => {
    setProdDefaults({
      ...prodDefaults,
      CustomName: e.target.value
    });
  };

  const onChangeCategory = (e) => {
    setProdDefaults({
      ...prodDefaults,
      Category: e.target.value
    });
  };

  const onChangeFeeOption = (e) => {
    setProdDefaults({
      ...prodDefaults,
      FeeOption: e.target.value
    });
  };

  const onChangeTaxRate = (e) => {
    setProdDefaults({
      ...prodDefaults,
      TaxRate: e.target.value
    });
  };

  const onClickSave = (e) => {
    e.preventDefault();

    const dataToSave = {...prodDefaults, MerchantId: storeDetails.MerchantId};

    if (prodDefaults.ProductDefaultId == 0 ) {
      dispatch(createProductDefaults(dataToSave));
    } else {
      dispatch(updateProductDefaults(dataToSave));
    }
  };

  const onBulkApplyFeeOption = async (e) => {
    e.preventDefault();
    setError('');

    const feeOptionName = getFeeOptionName(prodDefaults.FeeOption);
    const productLabel = getActiveProductLabel();
    const confirmed = window.confirm(
      `Apply "${feeOptionName}" to ${productLabel}? This updates every active product and cannot be undone automatically.`
    );

    if (!confirmed) {
      return;
    }

    setIsApplyingFeeOption(true);
    try {
      await dispatch(bulkApplyProductFeeOption({
        feeOption: prodDefaults.FeeOption,
        merchantId: storeDetails.MerchantId,
      }));
    } finally {
      setIsApplyingFeeOption(false);
    }
  };

  const onBulkApplyTaxRate = async (e) => {
    e.preventDefault();
    setError('');

    if (prodDefaults.TaxRate === '' || prodDefaults.TaxRate === null || Number.isNaN(Number(prodDefaults.TaxRate))) {
      setError('Please enter a valid default tax rate before applying to all products.');
      return;
    }

    const productLabel = getActiveProductLabel();
    const confirmed = window.confirm(
      `Apply ${prodDefaults.TaxRate}% tax rate to ${productLabel}? This updates every active product and cannot be undone automatically.`
    );

    if (!confirmed) {
      return;
    }

    setIsApplyingTaxRate(true);
    try {
      await dispatch(bulkApplyProductTaxRate({
        taxRate: prodDefaults.TaxRate,
        merchantId: storeDetails.MerchantId,
      }));
    } finally {
      setIsApplyingTaxRate(false);
    }
  };

  return (
    <div className={`bg-second p-4 ${s.card}`}>
      <h3 className="mt-0 mb-5 text-white">Default Product Details</h3>
      <form>
        <div className="row">
            <div className="col-md-12">
                <div className="form-group">
                    <label>Default Item Name<i className={`${s.description}`}> ( Used for Custom Charges) </i></label>
                    <input type="text" className="form-control" placeholder="" required="" value={prodDefaults.CustomName} onChange={onChangeProdName} />
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-md-6">
                <div className="form-group">
                    <label>Default Category</label>
                    <select className={`form-control ${s.select}`} value={prodDefaults.Category} onChange={onChangeCategory}>
                      {categoryList.map((category, i) => 
                          <option value={category.CategoryId} key={category.CategoryId}>{category.Name}</option>
                      )}
                    </select>
                </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                  <label>Default Card Fee Options</label>
                  <select className={`form-control ${s.select}`} value={prodDefaults.FeeOption} onChange={onChangeFeeOption}>
                  {mockFeeOptions.map((fee, i) => 
                      <option value={fee.Id} key={fee.Id}>{fee.Name}</option>
                  )}
                  </select>
              </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
                <label>Default Tax Rate %</label>
                <input type="number" className="form-control" placeholder="" required="" value={prodDefaults.TaxRate} onChange={onChangeTaxRate} />
            </div>
          </div>
        </div>

        {canBulkApply && (
          <div className={s.bulkActions}>
            <p className={s.bulkHelpText}>
              Apply the values currently shown above to every active product. This does not save the store defaults unless you also click Save Product Defaults.
            </p>
            <button
              type="button"
              className="btn btn-block"
              disabled={isApplyingFeeOption || isApplyingTaxRate}
              onClick={onBulkApplyFeeOption}
            >
              {isApplyingFeeOption ? 'Applying Card Fee...' : 'Apply Default Card Fee to All Products'}
            </button>
            <button
              type="button"
              className="btn btn-block mt-2"
              disabled={isApplyingFeeOption || isApplyingTaxRate}
              onClick={onBulkApplyTaxRate}
            >
              {isApplyingTaxRate ? 'Applying Tax Rate...' : 'Apply Default Tax Rate to All Products'}
            </button>
          </div>
        )}
        
        <div className="row alert-container">
        {
            error && (
                <Alert className="alert-sm" color="danger">
                    {error}
                </Alert>
            )
        }
        </div>
        <button type="submit" className="btn pt-2 btn-block" onClick={onClickSave}>Save Product Defaults</button>
    </form>
    </div>
  );
};

function mapStateToProps(state) {
  const authRoleState = buildAuthRoleState(state.auth);

  return {
    categories: state.products.categories,
    products: state.products.data,
    storeDetails: state.settings.storeDetails,
    productDefaults: state.settings.productDefaults,
    canBulkApply: canBulkApplyProductDefaults(authRoleState),
  };
}

export default withRouter(connect(mapStateToProps)(DefaultProduct));
